import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * A blank white page is the worst possible outcome for this learner, so any
 * render error is caught here and turned into a calm bilingual message with one
 * big button that reloads the app. Progress lives in localStorage, so reloading
 * never loses anything.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Left in on purpose: the caregiver may be asked to read this out.
    console.error('Unexpected error:', error, info.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <main id="main" className="app-main stack" style={{ paddingTop: 'var(--space-6)' }}>
        <section className="card stack" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '3rem' }} aria-hidden="true">
            🔧
          </div>
          <h1 style={{ marginBottom: 0 }}>حدث خطأ غير متوقع</h1>
          <p className="ltr" style={{ color: 'var(--text-muted)', fontWeight: 600 }}>
            Something went wrong
          </p>
          <p style={{ margin: 0 }}>
            تقدّمك محفوظ. اضغط الزر لإعادة تشغيل التطبيق.
            <span className="ltr" style={{ display: 'block', color: 'var(--text-muted)' }}>
              Your progress is safe. Press the button to restart the app.
            </span>
          </p>
          <button
            type="button"
            className="btn btn--primary btn--big btn--block"
            onClick={() => window.location.reload()}
          >
            <span className="btn__icon" aria-hidden="true">
              🔁
            </span>
            إعادة التشغيل · Restart
          </button>
          <details style={{ textAlign: 'start' }}>
            <summary style={{ cursor: 'pointer', color: 'var(--text-muted)' }}>
              <span className="ltr">Technical details</span>
            </summary>
            <pre
              className="ltr"
              style={{ whiteSpace: 'pre-wrap', fontSize: '0.8rem', color: 'var(--text-muted)' }}
            >
              {error.message}
            </pre>
          </details>
        </section>
      </main>
    );
  }
}
